import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import './AdminBackupDashboard.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const AdminBackupDashboard = () => {
  const [backupStatus, setBackupStatus] = useState(null);
  const [dbSize, setDbSize] = useState(null);
  const [loading, setLoading] = useState(true);
  const [backupLoading, setBackupLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(Date.now());

  // Fetch backup status and database size
  const fetchBackupStatus = async () => {
    try {
      const [statusRes, sizeRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/admin/backup-status`),
        axios.get(`${API_BASE_URL}/api/admin/database-size`)
      ]);

      if (statusRes.data.success) {
        setBackupStatus(statusRes.data.data);
      }

      if (sizeRes.data.success) {
        setDbSize(sizeRes.data.data);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching backup status:', error);
      toast.error('Không thể tải trạng thái backup');
      setLoading(false);
    }
  };

  // Trigger manual backup
  const triggerBackup = async (forceAll = false) => {
    if (!window.confirm(forceAll
      ? 'Bạn có chắc muốn backup TẤT CẢ dữ liệu không?'
      : 'Bạn có chắc muốn backup dữ liệu cũ hơn 7 ngày không?')) {
      return;
    }

    setBackupLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/admin/backup-now`, {
        forceAll
      });

      if (response.data.success) {
        toast.success(`Backup thành công! Đã backup ${response.data.data.postsBackedUp} posts`);
        // Refresh status after backup
        await fetchBackupStatus();
      } else {
        toast.error('Backup thất bại');
      }
    } catch (error) {
      console.error('Error triggering backup:', error);
      toast.error(error.response?.data?.message || 'Lỗi khi backup dữ liệu');
    } finally {
      setBackupLoading(false);
    }
  };

  // Auto refresh every 30 seconds
  useEffect(() => {
    fetchBackupStatus();

    const interval = setInterval(() => {
      fetchBackupStatus();
      setLastRefresh(Date.now());
    }, 300000); // 5 minutes (increased from 30s to save bandwidth)

    return () => clearInterval(interval);
  }, []);

  // Format timestamp
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Chưa có';
    return new Date(timestamp).toLocaleString('vi-VN');
  };

  // Format time remaining until next backup
  const getTimeUntilNextBackup = () => {
    if (!backupStatus?.nextScheduledBackup) return 'N/A';

    const now = Date.now();
    const next = backupStatus.nextScheduledBackup;
    const diff = next - now;

    if (diff < 0) return 'Sắp chạy...';

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    return `${days}d ${hours}h ${minutes}m`;
  };

  // Get database size percentage
  const getDbSizePercentage = () => {
    if (!dbSize) return 0;
    return dbSize.percentOf1GB || 0;
  };

  // Get database size color
  const getDbSizeColor = () => {
    const percent = getDbSizePercentage();
    if (percent < 50) return '#4caf50'; // Green
    if (percent < 75) return '#ff9800'; // Orange
    return '#f44336'; // Red
  };

  if (loading) {
    return (
      <div className="admin-backup-dashboard">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Đang tải dữ liệu...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-backup-dashboard">
      <div className="dashboard-header">
        <h1>🗄️ Quản Lý Backup & Database</h1>
        <div className="header-actions">
          <button
            className="btn-refresh"
            onClick={fetchBackupStatus}
            disabled={backupLoading}
          >
            🔄 Làm mới
          </button>
          <span className="last-refresh">
            Cập nhật: {formatDate(lastRefresh)}
          </span>
        </div>
      </div>

      {/* Database Size Monitor */}
      <div className="stats-grid">
        <div className="stat-card db-size-card">
          <h3>📊 Dung Lượng Database</h3>
          <div className="db-size-info">
            <div className="size-display">
              <span className="size-number">{dbSize?.megabytes || 0}</span>
              <span className="size-unit">MB</span>
            </div>
            <div className="size-bar-container">
              <div
                className="size-bar"
                style={{
                  width: `${getDbSizePercentage()}%`,
                  backgroundColor: getDbSizeColor()
                }}
              ></div>
            </div>
            <div className="size-details">
              <span>{getDbSizePercentage().toFixed(2)}% của 1GB (free tier)</span>
              <span>{dbSize?.gigabytes || 0} GB</span>
            </div>
          </div>
        </div>

        <div className="stat-card">
          <h3>⏰ Backup Tiếp Theo</h3>
          <div className="next-backup-info">
            <div className="time-remaining">
              {getTimeUntilNextBackup()}
            </div>
            <div className="backup-schedule">
              <p>📅 {formatDate(backupStatus?.nextScheduledBackup)}</p>
              <p className="schedule-note">Mỗi Chủ Nhật lúc 02:00 AM</p>
            </div>
          </div>
        </div>

        <div className="stat-card">
          <h3>📅 Backup Gần Nhất</h3>
          <div className="last-backup-info">
            {backupStatus?.lastBackupRun ? (
              <>
                <div className="backup-date">
                  {formatDate(backupStatus.lastBackupRun)}
                </div>
                <div className="backup-details">
                  <p>Week: {backupStatus.lastBackupWeek || 'N/A'}</p>
                </div>
              </>
            ) : (
              <p className="no-backup">Chưa có backup nào</p>
            )}
          </div>
        </div>

        <div className="stat-card">
          <h3>📈 Tổng Số Backup</h3>
          <div className="total-backups">
            <div className="backup-count">
              {backupStatus?.totalBackups || 0}
            </div>
            <p className="backup-count-label">lần backup</p>
          </div>
        </div>
      </div>

      {/* Cron Job Status */}
      <div className="cron-status-section">
        <h2>⚙️ Trạng Thái Cron Jobs</h2>
        <div className="cron-jobs-grid">
          <div className="cron-job-card">
            <div className="cron-status-indicator">
              <span className={`status-dot ${backupStatus?.cronJobs?.backupCronRunning ? 'active' : 'inactive'}`}></span>
              <span className="cron-name">Backup Cron</span>
            </div>
            <p className="cron-schedule">
              {backupStatus?.cronJobs?.cronSchedules?.backup || 'N/A'}
            </p>
          </div>

          <div className="cron-job-card">
            <div className="cron-status-indicator">
              <span className={`status-dot ${backupStatus?.cronJobs?.cleanupCronRunning ? 'active' : 'inactive'}`}></span>
              <span className="cron-name">Cleanup Cron</span>
            </div>
            <p className="cron-schedule">
              {backupStatus?.cronJobs?.cronSchedules?.cleanup || 'N/A'}
            </p>
          </div>
        </div>
      </div>

      {/* Manual Backup Actions */}
      <div className="backup-actions-section">
        <h2>🔧 Thao Tác Backup</h2>
        <div className="actions-grid">
          <div className="action-card">
            <h3>Backup Dữ Liệu Cũ</h3>
            <p>Backup các bài viết cũ hơn 7 ngày</p>
            <button
              className="btn-backup btn-primary"
              onClick={() => toast.info("Chức năng backup thủ công đang tạm khóa")}
              disabled={true}
              style={{ opacity: 0.5, cursor: 'not-allowed' }}
            >
              🚫 Backup (Đã khóa)
            </button>
          </div>

          <div className="action-card">
            <h3>Backup Toàn Bộ</h3>
            <p>Backup tất cả dữ liệu (bao gồm cả mới)</p>
            <button
              className="btn-backup btn-warning"
              onClick={() => toast.info("Chức năng backup thủ công đang tạm khóa")}
              disabled={true}
              style={{ opacity: 0.5, cursor: 'not-allowed' }}
            >
              🚫 Backup Tất Cả (Đã khóa)
            </button>
          </div>
        </div>
      </div>

      {/* Info Panel */}
      <div className="info-panel">
        <h3>ℹ️ Thông Tin</h3>
        <ul>
          <li>✅ Bài viết sẽ được lưu trữ tối đa <strong>7 ngày</strong> trong Realtime Database</li>
          <li>✅ Sau 7 ngày, bài viết sẽ được backup vào Firebase Storage và xóa khỏi DB</li>
          <li>✅ Thông tin profile, fanpage, WordPress configs <strong>KHÔNG BAO GIỜ</strong> bị xóa</li>
          <li>✅ Backup tự động chạy mỗi <strong>Chủ Nhật lúc 02:00 AM</strong></li>
          <li>✅ Cleanup tự động chạy mỗi <strong>ngày lúc 03:00 AM</strong></li>
        </ul>
      </div>
    </div>
  );
};

export default AdminBackupDashboard;
